import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';

export interface IState {
  loading: boolean;
  count: number;
  items: any[];
  selectedClient: any;
  users: any[];
  usersCount: number;
  invites: any[];
  invitesLoading: boolean;
}

export const clientEndpoints = {
  createClient: createAsyncThunk('client/createClient', async (payload: any) => {
    const response = await ApiService.post('/client/', payload);
    return response.data;
  }),
  // Accepts either a bare search string (existing call sites) or {search}. The object form lets
  // EntitySelect pass truthy params for an empty term, which it needs in order to pre-load
  // options; a bare '' makes it skip the fetch entirely. Interpolating an absent value also used
  // to send the literal `search=undefined`.
  getClientList: createAsyncThunk('client/getClientList', async (search?: any) => {
    const term = typeof search === 'object' && search !== null ? (search.search ?? '') : (search ?? '');
    const response = await ApiService.get(`/client/?search=${encodeURIComponent(term)}`);
    return response.data;
  }),
  deleteClient: createAsyncThunk('client/deleteClient', async (id: number) => {
    const response = await ApiService.delete(`/client/${id}`);
    return response.data;
  }),
  addUserToClient: createAsyncThunk('client/addUserToClient', async (payload: any) => {
    const response = await ApiService.post('/user_data/add_to_client/', payload);
    return response.data;
  }),
  getUserList: createAsyncThunk('client/getUserList', async (clientId: number) => {
    const response = await ApiService.get(`/user_data/?client_id=${clientId}`);
    return response.data;
  }),
  getClientInviteList: createAsyncThunk('client/getClientInviteList', async (clientId: number) => {
    const response = await ApiService.get(`/client_invite/?client_id=${clientId}`);
    return response.data;
  }),
  deleteClientInvite: createAsyncThunk('client/deleteClientInvite', async (clientInviteId: number) => {
    const response = await ApiService.delete(`/client_invite/${clientInviteId}`);
    return response.data;
  }),
  removeUserFromClient: createAsyncThunk('client/removeUserFromClient', async (payload: any) => {
    const response = await ApiService.post('/user_data/remove_from_client/', payload);
    return response.data;
  }),
};

export const clientSlice = createSlice({
  name: 'client',
  initialState: {
    loading: false,
    count: 0,
    items: [],
    selectedClient: null,
    users: [],
    usersCount: 0,
    invites: [],
    invitesLoading: false,
  } as IState,
  reducers: {
    setClient(state, {payload}) {
      state.selectedClient = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(clientEndpoints.getClientList.pending, state => {
        state.loading = true;
        state.items = [...state.items];
      })
      .addCase(clientEndpoints.getClientList.fulfilled, (state, {payload}) => {
        state.loading = false;
        state.items = payload.results;
        state.count = payload.count;
      })
      .addCase(clientEndpoints.deleteClient.fulfilled, (state, {payload}) => {
        const list = [...state.items];
        const index = list.findIndex(it => it.id === payload.data);
        if (index !== -1) {
          list.splice(index, 1);
        }
        state.items = list;
        state.count -= 1;
      })
      .addCase(clientEndpoints.getUserList.pending, state => {
        state.loading = true;
        state.users = [...state.users];
      })
      .addCase(clientEndpoints.getUserList.fulfilled, (state, {payload}) => {
        state.loading = false;
        state.users = payload.results;
        state.usersCount = payload.count;
      })
      .addCase(clientEndpoints.removeUserFromClient.fulfilled, (state, {payload}) => {
        const list = [...state.users];
        const index = list.findIndex(it => it.id === payload.id);
        if (index !== -1) {
          list.splice(index, 1);
        }
        state.users = list;
        state.usersCount -= 1;
      })
      .addCase(clientEndpoints.getClientInviteList.pending, state => {
        state.invitesLoading = true;
      })
      .addCase(clientEndpoints.getClientInviteList.fulfilled, (state, {payload}) => {
        state.invitesLoading = false;
        state.invites = payload.results;
      })
      .addCase(clientEndpoints.getClientInviteList.rejected, state => {
        state.invitesLoading = true;
      });
  },
});

export const clientActions = clientSlice.actions;

export type ClientSlice = {
  [clientSlice.name]: ReturnType<(typeof clientSlice)['reducer']>;
};
